<script setup lang="ts">
import { computed } from 'vue';
import { isThisWeek, getWeek, addWeeks, subWeeks } from 'date-fns';
import { useBookingResourcesStore } from '@/stores/bookingResources';
import { format } from '@/utils/date-fns';
import { IWeek } from '@/interfaces';

interface Props {
  selectedWeek: IWeek;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits(['week-changed', 'week-reset']);
const bookingResourcesStore = useBookingResourcesStore();

const canGoPrevious = computed(() => {
  return !isThisWeek(props.selectedWeek.dateFrom);
});

const weekNumber = computed(() => {
  return getWeek(props.selectedWeek.dateFrom);
});

const saveWeekToStore = (dateFrom: Date, dateTo: Date) => {
  bookingResourcesStore.setSelectedSlottedWeek({
    dateFrom,
    dateTo,
  });
};

async function getPreviousWeek() {
  if (!canGoPrevious.value) return;
  const newStart = subWeeks(props.selectedWeek.dateFrom, 1);
  const newEnd = subWeeks(props.selectedWeek.dateTo, 1);
  saveWeekToStore(newStart, newEnd);
  emit('week-changed', {
    dateFrom: format(newStart, 'yyyy-MM-dd'),
    dateTo: format(newEnd, 'yyyy-MM-dd'),
  });
}

async function getNextWeek() {
  const newStart = addWeeks(props.selectedWeek.dateFrom, 1);
  const newEnd = addWeeks(props.selectedWeek.dateTo, 1);
  saveWeekToStore(newStart, newEnd);
  emit('week-changed', {
    dateFrom: format(newStart, 'yyyy-MM-dd'),
    dateTo: format(newEnd, 'yyyy-MM-dd'),
  });
}

async function getThisWeek() {
  emit('week-reset');
}
</script>
<template>
  <div
    class="w-[275px] h-[72px] bg-neutral-0 bottom-[74px] sticky left-1/2 transform -translate-x-1/2 rounded-full px-4 py-3 flex justify-between items-center shadow-screenMedium"
  >
    <div
      class="w-[40px] h-[40px] rounded-full flex items-center justify-center border-1"
      :class="[
        canGoPrevious
          ? 'bg-neutral-100 border-neutral-300'
          : 'bg-neutral-100 border-neutral-300',
        canGoPrevious ? 'text-neutral-1000' : 'text-darkNeutral300A',
      ]"
      @click="getPreviousWeek"
    >
      <fa-icon :icon="['far', 'angle-left']" class="text-2xl" />
    </div>

    <div class="flex flex-col justify-center items-center">
      <p class="h600">{{ $t('global.week') }} {{ weekNumber }}</p>
      <p class="b300 underline" @click="getThisWeek">
        {{ $t('global.thisWeek') }}
      </p>
    </div>

    <div
      class="w-[40px] h-[40px] rounded-full flex items-center justify-center border-1 bg-neutral-100 border-neutral-300"
      @click="getNextWeek"
    >
      <fa-icon :icon="['far', 'angle-right']" class="text-2xl" />
    </div>
  </div>
</template>
